import axios from 'axios'

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api'

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  }
})

// Request interceptor
api.interceptors.request.use(
  (config) => {
    // Add auth token if available
    const token = localStorage.getItem('api_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => Promise.reject(error)
)

// Response interceptor
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const message = error.response?.data?.error || error.message || 'An error occurred'
    console.error('API Error:', message)
    return Promise.reject(error)
  }
)

// ==================== Admin API ====================

export const adminApi = {
  // Repository
  getRepositoryStatus: () => api.get('/admin/repository/status'),
  configureRepository: (path) => api.post(`/admin/repository/configure?path=${encodeURIComponent(path)}`),

  // Folder Browser
  getBrowseRoots: () => api.get('/admin/repository/browse/roots'),
  browseDirectory: (path) => api.get(`/admin/repository/browse?path=${encodeURIComponent(path)}`),
  getMounts: () => api.get('/admin/repository/mounts'),
  addMount: (windowsPath, label) => api.post(`/admin/repository/add-mount?windows_path=${encodeURIComponent(windowsPath)}${label ? `&label=${encodeURIComponent(label)}` : ''}`),
  deleteMount: (containerPath) => api.delete(`/admin/repository/mounts/${encodeURIComponent(containerPath.replace(/^\//, ''))}`),

  // Rescan / Re-extraction
  rescanRepository: (path) => api.post(`/admin/repository/rescan${path ? `?path=${encodeURIComponent(path)}` : ''}`),
  extractNewFiles: (path, useAi = true) => api.post(`/admin/repository/extract-new?use_ai_fallback=${useAi}${path ? `&path=${encodeURIComponent(path)}` : ''}`),
  reprocessAll: (path, useAi = true) => api.post(`/admin/repository/reprocess-all?use_ai_fallback=${useAi}${path ? `&path=${encodeURIComponent(path)}` : ''}`),
  clearAllExtractions: () => api.delete('/admin/repository/clear-all'),

  // Processing Jobs
  startProcessing: (config) => api.post('/admin/processing/start', config),
  listJobs: (params) => api.get('/admin/processing/jobs', { params }),
  getJob: (jobId) => api.get(`/admin/processing/jobs/${jobId}`),
  controlJob: (jobId, action) => api.post(`/admin/processing/jobs/${jobId}/control`, { action }),
  getJobLogs: (jobId, params) => api.get(`/admin/processing/jobs/${jobId}/logs`, { params }),

  // Statistics
  getProcessingStats: () => api.get('/admin/stats/processing'),
  getSpecificationStats: () => api.get('/admin/stats/specifications'),

  // Database
  vacuumDatabase: () => api.post('/admin/database/vacuum'),
  getDatabaseStats: () => api.get('/admin/database/stats'),

  // FTP Integration
  testFtpConnection: (config) => api.post('/admin/ftp/test-connection', config),
  browseFtpDirectory: (config, path) => api.post(`/admin/ftp/browse${path ? `?path=${encodeURIComponent(path)}` : ''}`, config),
  listFtpPdfs: (config) => api.post('/admin/ftp/list-pdfs', config),
  startFtpProcessing: (request) => api.post('/admin/ftp/process/start', request),
  cancelFtpProcessing: (jobId) => api.post(`/admin/ftp/process/cancel/${jobId}`),

  // Azure Blob Storage Integration
  testAzureConnection: () => api.get('/admin/azure/test-connection'),
  uploadToAzure: (file, folder = 'master', autoExtract = true) => {
    const formData = new FormData()
    formData.append('file', file)
    return api.post(`/admin/azure/upload?folder=${encodeURIComponent(folder)}&auto_extract=${autoExtract}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
  },
  listAzureFiles: (prefix = '') => api.get(`/admin/azure/list-files?prefix=${encodeURIComponent(prefix)}`),
  deleteAzureFile: (blobName, deleteFromDb = true) => api.delete(`/admin/azure/files/${encodeURIComponent(blobName)}?delete_from_db=${deleteFromDb}`),
  extractAzureFile: (blobName, useAi = true) => api.post(`/admin/azure/extract/${encodeURIComponent(blobName)}?use_ai=${useAi}`),

  // DigitalOcean Spaces Integration
  testSpacesConnection: () => api.get('/admin/spaces/test-connection'),
  uploadToSpaces: (file, folder = 'DieSIM/', extractImmediately = true) => {
    const formData = new FormData()
    formData.append('file', file)
    return api.post(`/admin/spaces/upload?folder=${encodeURIComponent(folder)}&extract_immediately=${extractImmediately}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
  },
  listSpacesFiles: (prefix = 'DieSIM/') => api.get(`/admin/spaces/list-files?prefix=${encodeURIComponent(prefix)}`),
  deleteSpacesFile: (objectKey, deleteFromDb = true) => api.delete(`/admin/spaces/files/${encodeURIComponent(objectKey)}?delete_from_db=${deleteFromDb}`),
  extractSpacesFile: (objectKey, useOcrFallback = true) => api.post(`/admin/spaces/extract/${encodeURIComponent(objectKey)}?use_ocr_fallback=${useOcrFallback}`),

  // ==================== Unified Storage API (Vendor-Agnostic) ====================
  // These endpoints work with any configured storage backend (Azure, Spaces, S3, etc.)

  storage: {
    // Get current storage provider information
    getInfo: () => api.get('/admin/storage/info'),

    // List all supported storage providers
    listSupportedProviders: () => api.get('/admin/storage/supported-providers'),

    // Test connection to current storage backend
    testConnection: () => api.get('/admin/storage/test-connection'),

    // List files in current storage backend
    listFiles: (prefix = '') => api.get(`/admin/storage/list-files?prefix=${encodeURIComponent(prefix)}`),

    // Upload file to current storage backend
    uploadFile: (file, folder = '', extractImmediately = true) => {
      const formData = new FormData()
      formData.append('file', file)
      return api.post(
        `/admin/storage/upload?folder=${encodeURIComponent(folder)}&extract_immediately=${extractImmediately}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      )
    },

    // Delete file from current storage backend
    deleteFile: (filePath, deleteFromDb = true) =>
      api.delete(`/admin/storage/files/${encodeURIComponent(filePath)}?delete_from_db=${deleteFromDb}`),

    // Extract data from file in current storage backend
    extractFile: (filePath, useOcrFallback = true) =>
      api.post(`/admin/storage/extract/${encodeURIComponent(filePath)}?use_ocr_fallback=${useOcrFallback}`)
  }
}

// ==================== Search API ====================

export const searchApi = {
  searchByUpload: (file, options = {}) => {
    const formData = new FormData()
    formData.append('file', file)
    Object.entries(options).forEach(([key, value]) => {
      formData.append(key, value)
    })
    return api.post('/search/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
  },

  searchManual: (params) => api.post('/search/manual', params),
  searchBySection: (sectionNumber, params) => api.get(`/search/section/${sectionNumber}`, { params }),
  submitFeedback: (feedback) => api.post('/search/feedback', feedback),
  getHistory: (params) => api.get('/search/history', { params }),
  getDownloadUrl: (filePath) => api.get(`/search/download-url?file_path=${encodeURIComponent(filePath)}`)
}

// ==================== Specifications API ====================

export const specificationsApi = {
  list: (params) => api.get('/specifications/', { params }),
  get: (id) => api.get(`/specifications/${id}`),
  getBySection: (sectionNumber) => api.get(`/specifications/section/${sectionNumber}`),
  create: (data) => api.post('/specifications/', data),
  update: (id, data) => api.put(`/specifications/${id}`, data),
  delete: (id, permanent = false) => api.delete(`/specifications/${id}?permanent=${permanent}`),
  restore: (id) => api.post(`/specifications/${id}/restore`),
  upload: (file, options = {}) => {
    const formData = new FormData()
    formData.append('file', file)
    return api.post('/specifications/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      params: options
    })
  },
  reprocess: (id, options = {}) => api.post(`/specifications/${id}/reprocess`, null, { params: options }),
  getRawText: (id) => api.get(`/specifications/${id}/raw-text`),
  markReview: (id, needsReview, notes) => api.post(`/specifications/${id}/mark-review`, null, {
    params: { needs_review: needsReview, notes }
  }),
  listAlloys: () => api.get('/specifications/alloys/list'),
  listProfileTypes: () => api.get('/specifications/profile-types/list'),
  compare: (id1, id2) => api.get(`/specifications/compare/${id1}/${id2}`)
}

// ==================== System API ====================

export const systemApi = {
  health: () => api.get('/health'),
  ready: () => api.get('/ready'),
  info: () => api.get('/system/info')
}

export default api
